import { useState } from "react";
import { toast } from "sonner";
import ThemeConstants from "../../../../../config/theme/theme_constants";
import { QuestionInterface } from "../../../domain/interfaces/question_interface";
import TextAreaComment from "./textarea_comment";
import { AnswerInterface } from "../../../domain/interfaces/answer_interface";
import { useTranslation } from "../../../../../i18n/useTranslation";

interface BooleanQuestionProps {
    question: QuestionInterface;
    color: string;
    onValidate: (answer: AnswerInterface, initialAnswer?: AnswerInterface) => void;
}

const BooleanQuestion: React.FC<BooleanQuestionProps> = ({ question, color, onValidate }) => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    const [ userAnswer, setUserAnswer ] = useState<boolean | undefined>(question.initialAnswer?.answer as boolean | undefined);
    const [ comment, setComment ] = useState<string>(question.initialAnswer?.text || '');

    const trueColor = color === theme.UvibesGreen ? theme.UvibesBlue : theme.UvibesGreen;
    const falseColor = color === theme.UvibesPink ? theme.UvibesOrange : theme.UvibesPink;

    return (
        <div className="flex flex-col items-center gap-5 max-w-94.5 w-full">
            <p
                className="bg-white font-bold text-xl leading-tight px-9 py-4 text-center rounded-[20px] aspect-3/2 flex items-center justify-center w-full"
                style={{ 
                    color: color,
                    boxShadow: '0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)',
                }}
            >
                {question.question}
            </p>

            <form
                id={`question-${question.id}`}
                onSubmit={(e) => {
                    e.preventDefault();
                    if (userAnswer === undefined) {
                        toast.error(t('survey.answer_required'));
                        return;
                    }
                    onValidate({
                        questionId: question.id,
                        answer: userAnswer,
                        text: comment
                    }, question.initialAnswer);
                }}
                className="flex items-center justify-between w-full gap-5"
            >
                <button
                    type="button"
                    className="rounded-full font-bold text-xl flex-1 py-1.75 px-3 transition-all duration-200 ease-out hover:scale-105 cursor-pointer"
                    style={{
                        color: userAnswer === true ? 'white' : trueColor,
                        backgroundColor: userAnswer === true ? trueColor : 'white',
                        boxShadow: userAnswer === true
                            ? `0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08), inset 0 0 0 4px ${trueColor}`
                            : '0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)',
                    }}
                    onMouseEnter={(e) => {
                        if (userAnswer !== true) {
                            e.currentTarget.style.boxShadow = `0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08), inset 0 0 0 4px ${trueColor}80`;
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (userAnswer !== true) {
                            e.currentTarget.style.boxShadow = '0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)';
                        }
                    }}
                    onClick={() => setUserAnswer(true)}
                >
                    {t('common.button.yes')}
                </button>

                <button
                    type="button"
                    className="rounded-full font-bold text-xl flex-1 py-1.75 px-3 transition-all duration-200 ease-out hover:scale-105 cursor-pointer"
                    style={{
                        color: userAnswer === false ? 'white' : falseColor,
                        backgroundColor: userAnswer === false ? falseColor : 'white',
                        boxShadow: userAnswer === false
                            ? `0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08), inset 0 0 0 4px ${falseColor}`
                            : '0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)',
                    }}
                    onMouseEnter={(e) => {
                        if (userAnswer !== false) {
                            e.currentTarget.style.boxShadow = `0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08), inset 0 0 0 4px ${falseColor}80`;
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (userAnswer !== false) {
                            e.currentTarget.style.boxShadow = '0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)';
                        }
                    }}
                    onClick={() => setUserAnswer(false)}
                >
                    {t('common.button.no')}
                </button>
            </form>

            <TextAreaComment rows={3} comment={comment} onCommentChange={(value: string) => setComment(value)} />
        </div>
    );
}

export default BooleanQuestion;